/**
 * sessionNotificationAffordance — what a "Live now" row should actually offer.
 *
 * `session_started` is the only `urgent` notification the product sends: title
 * "Live now", body "Session ready: … · Join now", action "Join". Nothing on the
 * bell or the notification centre ever checked whether the room it points at is
 * still open, so a row that invites you to join a session which finished the day
 * before is visually identical to one for a session starting right now (prod,
 * FBD workspace, 2026-09-01 — all seven of one user's unread "Live now" rows
 * pointed at DRAFT or RUN_COMPLETE sessions).
 *
 * The fan-out itself no longer fires at a session that isn't live
 * (`notifySessionLiveNow`), but rows already in the table outlive their session,
 * and a session can close while its notification sits unread. So the affordance
 * is resolved at render time from the target's current state rather than from
 * the fact that the row exists.
 */

import type { SessionJoinability } from './notificationService';

const SESSION_ID_IN_URL = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/**
 * The session a notification is about: the `data.sessionId` the sender wrote,
 * falling back to the uuid in its deep link for rows written before that.
 */
export function sessionIdFromNotification(params: {
  data?: Record<string, unknown> | null;
  actionUrl?: string | null;
}): string | null {
  const fromData = params.data?.sessionId;
  if (typeof fromData === 'string' && fromData.trim().length > 0) return fromData;
  const match = params.actionUrl?.match(SESSION_ID_IN_URL);
  return match ? match[0] : null;
}

/** Events whose action promises a live room. */
export function isJoinPromiseEvent(event?: string | null): boolean {
  return event === 'session_started' || event === 'session_starting_soon';
}

export interface SessionNotificationAffordance {
  /** Text for the action link. */
  actionLabel: string;
  /** Where the action goes. */
  actionUrl: string;
  /** Short state chip to render beside the row, when the room is not open. */
  statusChip: string | null;
  /** True when the row still reads as urgent but its target has moved on. */
  isStale: boolean;
}

/**
 * Resolve the action a session notification should offer, given what its target
 * session is doing right now. Anything we can't resolve (`unknown`, or a
 * non-session notification) is left exactly as the sender wrote it.
 */
export function resolveSessionNotificationAffordance(params: {
  event?: string | null;
  actionUrl?: string | null;
  actionLabel?: string | null;
  sessionId?: string | null;
  joinability?: SessionJoinability;
}): SessionNotificationAffordance | null {
  const fallbackUrl = params.actionUrl ?? null;
  if (!fallbackUrl) return null;

  const base: SessionNotificationAffordance = {
    actionLabel: params.actionLabel?.trim() || 'View',
    actionUrl: fallbackUrl,
    statusChip: null,
    isStale: false,
  };

  if (!isJoinPromiseEvent(params.event) || !params.sessionId) return base;

  switch (params.joinability) {
    case 'ended':
      return {
        // The run is over — the useful thing behind this row is the result.
        actionLabel: 'View results',
        actionUrl: `/prioritization/sessions/${params.sessionId}`,
        statusChip: 'Session ended',
        isStale: true,
      };
    case 'not_started':
      return {
        actionLabel: 'Open session',
        actionUrl: `/prioritization/sessions/${params.sessionId}`,
        statusChip: 'Not started yet',
        isStale: true,
      };
    case 'live':
    case 'unknown':
    default:
      return base;
  }
}
